import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { components, MultiValue, OptionProps, SingleValue } from 'react-select'
import {
  ReactSelectProps,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { Badge, BadgeProps } from '../badge'
import { twMerge } from 'tailwind-merge'
import { getLocalesOptions } from './utils/get-locales-options'

export type LocaleValue = PageLocaleEnum

type onChangeType<IsMulti extends boolean> = IsMulti extends true
  ? (locale: LocaleValue[]) => void
  : (locale: LocaleValue) => void

export type LocalesOptionType = {
  value: LocaleValue
  label: string
  optionBadgeProps?: BadgeProps
  nonRemovable?: boolean
}

export interface LanguageSelectorProps<
  IsMulti extends boolean = false,
  Option extends LocalesOptionType = LocalesOptionType,
> extends Omit<
    ReactSelectProps<LocaleValue, IsMulti, Option>,
    'options' | 'onChange' | 'selectedOption' | 'components'
  > {
  selectedLocales: IsMulti extends true ? LocaleValue[] : LocaleValue
  options?: LocalesOptionType[]
  onChange: onChangeType<IsMulti>
}

export const LanguageSelector = <IsMulti extends boolean = false>({
  selectedLocales,
  options,
  onChange,
  isMulti,
  ...props
}: LanguageSelectorProps<IsMulti>) => {
  const { t } = useTranslation()

  const localesOptions = useMemo(() => {
    if (options?.length) {
      return options
    } else {
      return getLocalesOptions({ t })
    }
  }, [t, options])

  const onChangeMulti = (option: MultiValue<LocalesOptionType>) => {
    ;(onChange as onChangeType<true>)(option.map(option => option.value))
  }

  const onChangeSingle = (option: SingleValue<LocalesOptionType>) => {
    ;(onChange as onChangeType<false>)(option?.value as LocaleValue)
  }

  const Option = <IsMulti extends boolean, Option extends LocalesOptionType>(
    props: OptionProps<Option, IsMulti>,
  ) => {
    const { label, className, data } = props
    const { optionBadgeProps } = data
    return (
      <components.Option
        {...props}
        className={twMerge(
          className,
          optionBadgeProps && '!flex items-center gap-3',
        )}
      >
        {label}
        {optionBadgeProps && <Badge {...optionBadgeProps} />}
      </components.Option>
    )
  }

  return isMulti ? (
    <SelectNew<LocaleValue, true, LocalesOptionType>
      isMulti
      onChange={onChangeMulti}
      selectedOption={localesOptions.filter(option =>
        (selectedLocales as LocaleValue[]).includes(option.value),
      )}
      options={localesOptions}
      components={{ Option }}
      {...props}
    />
  ) : (
    <SelectNew<LocaleValue, false, LocalesOptionType>
      onChange={onChangeSingle}
      selectedOption={
        localesOptions.find(option => option.value === selectedLocales) ?? null
      }
      options={localesOptions}
      components={{ Option }}
      {...props}
    />
  )
}
